import React, { useState, useEffect, useRef } from 'react';
import { MapPinIcon, TagIcon, TicketIcon, UserGroupIcon, CalendarIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { getProductRepresentativeImage, deleteTourProduct, getProductNote, saveProductNote } from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';

// Simple in-memory cache to avoid re-fetching the same product image repeatedly
const repImageCache = new Map();

const ProductCard = ({ product, onSettingsClick, onToggleSale, onPriceStockClick, onNoteSaved = null }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef(null);
  const hasRequestedRef = useRef(false);
  // Note modal state
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    if (!product?.상품번호) return;
    const key = String(product.상품번호);
    // If cached, use immediately
    if (repImageCache.has(key)) {
      setImageUrl(repImageCache.get(key));
      return;
    }
    const el = cardRef.current;
    if (!el) return;

    const onIntersect = async (entries, observer) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting && !hasRequestedRef.current) {
          hasRequestedRef.current = true;
          try {
            const { companyNo } = getUserInfo() || {};
            if (!companyNo) return; // cannot fetch without supplier/company
            const url = await getProductRepresentativeImage({ companyNo, productNo: product.상품번호 });
            if (url) {
              repImageCache.set(key, url);
              setImageUrl(url);
            } else {
              repImageCache.set(key, null);
            }
          } catch (e) {
            // swallow; keep placeholder
          } finally {
            observer.disconnect();
          }
        }
      });
    };

    const io = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: '200px',
      threshold: 0.01,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [product?.상품번호]);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  // 컴포넌트 로직
  const noEvents = (() => {
    try {
      const raw = product?.최종행사일;
      if (!raw) return true;
      const s = String(raw).trim();
      const d = new Date(s.includes('T') ? s : `${s}T23:59:59`);
      if (isNaN(d.getTime())) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    } catch {
      return true;
    }
  })();

  const openNoteModal = async () => {
    if (!product) return;
    setNoteLoading(true);
    setNoteModalOpen(true);
    try {
      const fetched = await getProductNote({ productNo: product?.상품번호 || product?.상품코드 });
      const initial = fetched || product?.상품비고 || '';
      setNoteText(String(initial || ''));
    } catch (e) {
      setNoteText(String(product?.상품비고 || ''));
    } finally {
      setNoteLoading(false);
    }
  };

  const saveNote = async () => {
    if (!product) return;
    try {
      setNoteSaving(true);
      await saveProductNote({ productNo: product?.상품번호 || product?.상품코드, note: noteText });
      if (onNoteSaved) onNoteSaved(product, noteText);
      setNoteModalOpen(false);
    } catch (e) {
      alert('상품비고 저장에 실패했습니다.');
    } finally {
      setNoteSaving(false);
    }
  };

  return (
    <div 
      ref={cardRef}
      className={`relative rounded-sm border border-gray-100 overflow-visible hover:shadow-md transition-shadow duration-300 ${noEvents ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-100'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 상품 상태 표시 및 제어 (상단 우측) */}
      <div className="relative bg-white">
        <div className="absolute top-0 right-0 m-2 z-30" ref={menuRef}>
          <div className="relative">
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
              className={`border border-gray-200 text-[10px] px-2 py-1 rounded focus:outline-none bg-white inline-flex items-center gap-1 ${product.판매여부 ? 'text-green-800' : 'text-red-500'}`}
            >
              {product.판매여부 ? '판매중' : '판매중지'}
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded shadow-lg z-30 py-1" role="listbox">
                <button
                  type="button"
                  className="block w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); if (onToggleSale) onToggleSale(product, true); }}
                >
                  판매중
                </button>
                <button
                  type="button"
                  className="block w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); if (onToggleSale) onToggleSale(product, false); }}
                >
                  판매중지
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button
                  type="button"
                  className="block w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-gray-50"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setShowDeleteModal(true); }}
                >
                  상품삭제
                </button>
              </div>
            )}
          </div>
        </div>
        {noEvents && (
          <div className="absolute mt-0.5 left-[10px] m-2 z-30">
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-red-400 border border-gray-200">
              판매가능 행사 없음
            </span>
          </div>
        )}

        {/* 상품 이미지 + 정보 */}
        <div className="flex p-4 gap-3">
          {/* Left thumbnail */}
          <div className="relative w-28 h-26 rounded-md bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.상품명 || '대표 이미지'}
                className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-full h-full" />
            )}
            {isHovered && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex flex-col gap-1 pointer-events-auto">
                  <button 
                    onClick={() => onSettingsClick(product)}
                    className="bg-white/90 backdrop-blur text-gray-800 text-[10px] px-2 py-1 rounded border border-gray-200 hover:bg-white shadow-sm"
                    title="기본설정"
                  >기본설정</button>
                  <button
                    onClick={() => {
                      if (onPriceStockClick) return onPriceStockClick(product);
                      navigate(`/price-stock/${product.상품번호}`);
                    }}
                    className="bg-white/90 backdrop-blur text-gray-800 text-[10px] px-2 py-1 rounded border border-gray-200 hover:bg-white shadow-sm"
                    title="가격/재고"
                  >가격/재고</button>
                  <button
                    onClick={() => navigate(`/detail-settings/${product.상품번호}`)}
                    className="bg-white/90 backdrop-blur text-gray-800 text-[10px] px-2 py-1 rounded border border-gray-200 hover:bg-white shadow-sm"
                    title="상세설정"
                  >상세설정</button>
                </div>
              </div>
            )}
          </div>

          {/* Right info */}
          <div className="flex-1 min-w-0">
            {/* 상품 코드와 타입 */}
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">코드: {product.상품코드}</span>
              <div className="flex items-center">
                <TagIcon className="w-3 h-3 mr-1 text-gray-400" />
                <span className="text-xs text-gray-500">{product.상품종류 || product.상품타입}</span>
              </div>
            </div>
            
            {/* 상품명 (클릭 시 주문내역 리스트 페이지 이동) */}
            <h3
              className="relative z-20 text-base font-medium text-gray-900 hover:underline cursor-pointer mb-2 truncate"
              title="해당 상품의 주문 내역 보기"
              onClick={(e) => {
                e.stopPropagation();
                const productNo = product?.상품번호;
                if (productNo) {
                  navigate(`/orders/${encodeURIComponent(productNo)}`, { state: { productName: product.상품명, productNo } });
                }
              }}
            >
              {product.상품명}
            </h3>
            
              {/* 위치 정보 + 상품종류/타입 배지 (같은 라인) */}
            <div className="flex items-center justify-between text-gray-600 mb-2">
              <div className="flex items-center">
                <MapPinIcon className="w-4 h-4 mr-1 text-gray-400" />
                <span className="text-xs">{product.도시} {product.국가 ? `(${product.국가})` : ''}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {product.상품종류 && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                    <TagIcon className="w-3 h-3 text-gray-400" /> {product.상품종류}
                  </span>
                )}
                {product.상품타입 && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                    <TicketIcon className="w-3 h-3 text-gray-400" /> {product.상품타입}
                  </span>
                )}
                {product.상품뱃지 && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {product.상품뱃지}
                  </span>
                )}
              </div>
            </div>
            
            {/* 추가 정보 - 기간, 인원 등 */}
            <div className="flex flex-wrap gap-2.5 mb-0">
              {product.기간 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700">
                  <CalendarIcon className="w-4 h-4 text-gray-400" /> {product.기간}
                </span>
              )}
              
              {product.최소인원 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700">
                  <UserGroupIcon className="w-4 h-4 text-gray-400" /> 최소 {product.최소인원}명
                </span>
              )}
            </div>

            {/* 상품 비고 프리뷰 + 편집 */}
            <div className="mt-2">
              <div className="flex items-start gap-1 text-[12px] text-gray-700">
                <button
                  type="button"
                  className="text-left flex-1 min-w-0 cursor-pointer hover:underline decoration-dotted"
                  title="상품비고 편집"
                  aria-label="상품비고 편집"
                  onClick={(e) => { e.stopPropagation(); openNoteModal(); }}
                >
                  <span className="line-clamp-2 whitespace-pre-line">
                    {product?.상품비고 && String(product.상품비고).trim() ? product.상품비고 : '상품비고 없음'}
                  </span>
                </button>
                <svg className="mt-[2px] h-3.5 w-3.5 text-gray-400 opacity-80" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M17.414 2.586a2 2 0 0 0-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 0 0 0-2.828z"/>
                  <path d="M5 15a1 1 0 0 0 1 1h9a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3V6a1 1 0 1 1 2 0v9z"/>
                </svg>
              </div>
            </div>

          </div>
        </div>
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 z-[9998]" onMouseDown={() => setShowDeleteModal(false)}>
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl w-[520px] max-w-[92vw]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">상품 삭제</h3>
              <p className="mt-1 text-xs text-red-600">삭제된 상품은 복원할 수 없습니다. 정말 삭제하시겠습니까?</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-sm text-gray-700">상품명</div>
              <div className="px-3 py-2 border border-gray-200 rounded bg-gray-50 text-sm text-gray-800">{product?.상품명 ?? '-'}</div>
              <div>
                <label className="block text-sm font-medium text-gray-700">확인을 위해 아래에 '삭제'를 입력하세요</label>
                <input
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="삭제"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-gray-50 rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm rounded ${deleteConfirmText==='삭제' && !deleting ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300'} text-white`}
                disabled={deleteConfirmText !== '삭제' || deleting}
                onClick={async () => {
                  if (deleteConfirmText !== '삭제') return;
                  try {
                    setDeleting(true);
                    await deleteTourProduct({ productNo: product?.상품번호 });
                    alert('상품이 삭제되었습니다.');
                    window.location.reload();
                  } catch (err) {
                    console.error('상품 삭제 실패', err);
                    alert('상품 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? '삭제 중...' : '영구 삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
      {noteModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setNoteModalOpen(false)} />
          <div className="relative z-10 w-[min(720px,95vw)] max-h-[90vh] overflow-auto bg-white rounded-xl shadow-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-800">상품비고 편집</h3>
              <button type="button" className="px-2 py-1 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setNoteModalOpen(false)}>닫기</button>
            </div>
            <div className="mb-3 text-sm text-gray-600">
              <div><span className="text-gray-500">상품번호:</span> <span className="font-medium">{product?.상품번호 || product?.상품코드 || '-'}</span></div>
              <div className="truncate"><span className="text-gray-500">상품명:</span> <span className="font-medium">{product?.상품명 || '-'}</span></div>
            </div>
            <div>
              {noteLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">불러오는 중...</div>
              ) : (
                <textarea
                  className="w-full h-64 border rounded-lg p-3 text-sm"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="상품비고를 입력하세요"
                />
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="px-3 py-2 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setNoteModalOpen(false)}>취소</button>
              <button type="button" className={`px-3 py-2 text-sm rounded ${noteSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`} onClick={saveNote} disabled={noteSaving}>{noteSaving ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductCard;
